/**
 * The SKU rows behind a stocktake location.
 *
 * These lived as a module-local constant inside `StocktakeLocationDetailScreen`
 * and were location-agnostic: the same six rows rendered for every
 * `:locationCode`. The cross-location SKU search on the Progress tab needs the
 * same rows tagged with a location, so they moved here rather than being
 * duplicated.
 *
 * `skusForLocation` still returns all six rows for ANY code — the mock is
 * deliberately unchanged, because the detail screen's tests pin these strings.
 */

export interface SkuRow {
  sku: string
  name: string
  qty: number
  value: string
  uom: 'EA' | 'CAR' | 'KG'
  exception?: string
  staff: string
}

/** A SKU row as the cross-location search sees it: which location it came from. */
export interface LocatedSkuRow extends SkuRow {
  location: string
}

export const LOCATION_SKUS: SkuRow[] = [
  {
    sku: '13052524',
    name: 'Nestlé Milo Chocolate Malt Drink Powder',
    qty: 150,
    value: '$2,300.00',
    uom: 'EA',
    exception: 'Invalid barcode',
    staff: 'Alex',
  },
  {
    sku: '13052525',
    name: 'Ovaltine Rich Chocolate Malt Drink',
    qty: 120,
    value: '$1,500.00',
    uom: 'EA',
    staff: 'Sarah',
  },
  {
    sku: '13052526',
    name: 'Horlicks Malted Milk Drink',
    qty: 90,
    value: '$1,200.00',
    uom: 'EA',
    staff: 'Tom',
  },
  {
    sku: '13052527',
    name: 'Milo 3-in-1 Instant Beverage Mix',
    qty: 200,
    value: '$3,000.00',
    uom: 'EA',
    exception: 'Not in this stocktake',
    staff: 'Jessica',
  },
  {
    sku: '13052528',
    name: 'Cadbury Drinking Chocolate',
    qty: 80,
    value: '$1,100.00',
    uom: 'EA',
    staff: 'Michael',
  },
  {
    sku: '13052529',
    name: 'Dairy Milk Chocolate Drink Mix',
    qty: 110,
    value: '$1,700.00',
    uom: 'EA',
    staff: 'Nina',
  },
]

export function skusForLocation(_locationCode: string): SkuRow[] {
  return LOCATION_SKUS
}

/**
 * Every counted SKU across every location, which is what the Progress tab's
 * magnifier searches. Built from the location codes rather than hand-authored
 * so it cannot drift from the Progress table.
 */
export function allLocationSkus(locationCodes: readonly string[]): LocatedSkuRow[] {
  return locationCodes.flatMap((location) =>
    skusForLocation(location).map((row) => ({ ...row, location })),
  )
}

/** Matches on SKU code, description, or the staff who counted it. */
export function matchesSkuQuery(row: SkuRow, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (q === '') return true
  return (
    row.sku.toLowerCase().includes(q) ||
    row.name.toLowerCase().includes(q) ||
    row.staff.toLowerCase().includes(q)
  )
}
